/* eslint-disable @typescript-eslint/no-explicit-any */

import { Flex, Text, List, Box } from '@chakra-ui/react';
import { Envelope, LinkedinLogo, MapPin, Phone } from '@phosphor-icons/react';
import { JSX, forwardRef } from 'react';

const Resume = forwardRef<HTMLDivElement, { resume: Resume }>(function Resume({ resume }, ref) {
  return (
    <Flex
      ref={ref}
      bg="white"
      h="auto"
      borderRadius="10px"
      p={{ base: '16px', md: '24px' }}
      justifyContent="center"
      alignItems="center"
      border="1px solid"
      borderColor="neutrals.200"
    >
      <Flex flexDirection="column" w="100%" px={{ base: '8px', md: '24px' }}>
        <Flex flexDirection="column" alignItems="center">
          <Text
            fontSize={{ base: '24px', md: '32px' }}
            fontWeight="bold"
            textAlign="center"
            color="#323332"
            lineHeight="1.2"
          >
            {resume.personal_information.name}
          </Text>
          <table
            style={{
              marginTop: '10px',
              fontSize: '12px',
              color: '#838383',
              borderCollapse: 'collapse',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}
          >
            <tbody>
              <tr>
                {resume.personal_information.linkedin && (
                  <>
                    <td
                      data-pdf-hide
                      style={{ verticalAlign: 'middle', whiteSpace: 'nowrap', paddingRight: '3px' }}
                    >
                      <LinkedinLogo size={13} color="#838383" />
                    </td>
                    <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <a
                        href={
                          resume.personal_information.linkedin.startsWith('http')
                            ? resume.personal_information.linkedin
                            : `https://${resume.personal_information.linkedin}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#838383', textDecoration: 'none' }}
                      >
                        {resume.personal_information.linkedin.replace('https://www.', '')}
                      </a>
                    </td>
                    {(resume.personal_information.email ||
                      resume.personal_information.phone ||
                      resume.personal_information.location) && (
                      <td style={{ verticalAlign: 'middle', color: '#b0b0b0', padding: '0 8px' }}>
                        •
                      </td>
                    )}
                  </>
                )}
                {resume.personal_information.email && (
                  <>
                    <td
                      data-pdf-hide
                      style={{ verticalAlign: 'middle', whiteSpace: 'nowrap', paddingRight: '3px' }}
                    >
                      <Envelope size={13} color="#838383" />
                    </td>
                    <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <a
                        href={`mailto:${resume.personal_information.email}`}
                        style={{ color: '#838383', textDecoration: 'none' }}
                      >
                        {resume.personal_information.email}
                      </a>
                    </td>
                    {(resume.personal_information.phone ||
                      resume.personal_information.location) && (
                      <td style={{ verticalAlign: 'middle', color: '#b0b0b0', padding: '0 8px' }}>
                        •
                      </td>
                    )}
                  </>
                )}
                {resume.personal_information.phone && (
                  <>
                    <td
                      data-pdf-hide
                      style={{ verticalAlign: 'middle', whiteSpace: 'nowrap', paddingRight: '3px' }}
                    >
                      <Phone size={13} color="#838383" />
                    </td>
                    <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {resume.personal_information.phone}
                    </td>
                    {resume.personal_information.location && (
                      <td style={{ verticalAlign: 'middle', color: '#b0b0b0', padding: '0 8px' }}>
                        •
                      </td>
                    )}
                  </>
                )}
                {resume.personal_information.location && (
                  <>
                    <td
                      data-pdf-hide
                      style={{ verticalAlign: 'middle', whiteSpace: 'nowrap', paddingRight: '3px' }}
                    >
                      <MapPin size={13} color="#838383" />
                    </td>
                    <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {resume.personal_information.location}
                    </td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </Flex>
        {resume.summary && (
          <Text
            fontSize={{ base: '12px', md: '14px' }}
            color="neutrals.700"
            lineHeight="1.6"
            mt={{ base: '8px', md: '20px' }}
            textAlign="start"
          >
            {resume.summary}
          </Text>
        )}
        <Box
          as="hr"
          width="100%"
          border="none"
          borderTop="1px solid"
          borderColor="neutrals.200"
          my={{ base: '8px', md: '12px' }}
        />
        <Section
          title="Professional Experience"
          items={resume.work_experiences}
          renderItem={renderExperience}
        />
        <Box
          as="hr"
          width="100%"
          border="none"
          borderTop="1px solid"
          borderColor="neutrals.200"
          my={{ base: '8px', md: '12px' }}
        />
        <Section title="Education" items={resume.educations} renderItem={renderEducation} />
        <Box
          as="hr"
          width="100%"
          border="none"
          borderTop="1px solid"
          borderColor="neutrals.200"
          my={{ base: '8px', md: '12px' }}
        />
        {resume.skills.length > 0 && (
          <Section title="Skills" items={resume.skills} renderItem={renderSkill} />
        )}
        {resume.other_information.length > 0 && (
          <Section
            title="Other Information"
            items={resume.other_information}
            renderItem={renderOtherInformation}
          />
        )}
      </Flex>
    </Flex>
  );
});

export default Resume;

function Section({
  title,
  items,
  renderItem
}: {
  title: string;
  items: WorkExperience[] | Education[] | OtherInformation[];
  renderItem: (item: any, index: number) => JSX.Element;
}) {
  return (
    <Flex flexDirection="column">
      <Text
        color="#323332"
        fontSize={{ base: '16px', md: '20px' }}
        fontWeight="bold"
        mb={{ base: '12px', md: '16px' }}
      >
        {title}
      </Text>
      {items.length > 0 ? (
        items.map(renderItem)
      ) : (
        <Text color="neutrals.500" fontSize={{ base: '12px', md: '14px' }}>
          No information available
        </Text>
      )}
    </Flex>
  );
}

function renderExperience(experience: WorkExperience, index: number) {
  return (
    <Flex key={index} flexDirection="column" mb={{ base: '12px', md: '16px' }}>
      <Flex
        justifyContent="space-between"
        direction={{ base: 'column', sm: 'row' }}
        gap={{ base: '2px', sm: '0' }}
      >
        <Text fontWeight="bold" color="neutrals.700" fontSize={{ base: '14px', md: '16px' }}>
          {experience.job_role}
        </Text>
        <Text fontWeight="bold" color="neutrals.700" fontSize={{ base: '12px', md: '14px' }}>
          {experience.company_name}
        </Text>
      </Flex>
      <Box h={{ base: '2px', md: '4px' }} />
      <Text fontSize={{ base: '12px', md: '14px' }} color="neutrals.500" lineHeight="1.4">
        {experience.location ? `${experience.location} | ${experience.time}` : experience.time}{' '}
        {experience.duration && `• ${experience.duration}`}
      </Text>
      <Box h={{ base: '6px', md: '8px' }} />
      <List.Root
        pl={{ base: '16px', md: '20px' }}
        fontSize={{ base: '12px', md: '14px' }}
        color="neutrals.900"
      >
        {experience.description.map((desc, descIndex) => (
          <List.Item key={descIndex} lineHeight="1.4">
            {desc}
          </List.Item>
        ))}
      </List.Root>
    </Flex>
  );
}

function renderEducation(education: Education, index: number) {
  return (
    <Flex key={index} flexDirection="column" mb={{ base: '12px', md: '16px' }}>
      <Flex justifyContent="space-between">
        <Text fontSize={{ base: '12px', md: '14px' }} fontWeight="semibold" color="neutrals.700">
          {education.school_name}
        </Text>
        {education.location && (
          <Text fontSize={{ base: '12px', md: '14px' }} color="neutrals.500">
            {education.location}
          </Text>
        )}
      </Flex>
      <Box h={{ base: '2px', md: '4px' }} />
      <Text fontSize={{ base: '10px', md: '12px' }} color="neutrals.500" lineHeight="1.4">
        {education.degree}
        {education.department && ` in ${education.department}`}
        {education.time && ` | ${education.time}`}
      </Text>
    </Flex>
  );
}

function renderSkill(skill: Skill, index: number) {
  return (
    <Flex key={index} flexDirection="column" mb={{ base: '12px', md: '16px' }}>
      {skill.section.toLowerCase() !== 'skills' && (
        <Text fontWeight="bold" color="neutrals.700" fontSize={{ base: '12px', md: '14px' }}>
          {skill.section}
        </Text>
      )}
      <Box h={{ base: '6px', md: '8px' }} />
      <List.Root
        pl={{ base: '16px', md: '20px' }}
        fontSize={{ base: '11px', md: '14px' }}
        color="neutrals.900"
      >
        {skill.contents.map((content, contentIndex) => (
          <List.Item key={contentIndex} lineHeight="1.4">
            {content}
          </List.Item>
        ))}
      </List.Root>
    </Flex>
  );
}

function renderOtherInformation(information: OtherInformation, index: number) {
  return (
    <Flex key={index} flexDirection="column" mb={{ base: '12px', md: '16px' }}>
      <Text fontWeight="bold" color="neutrals.700" fontSize={{ base: '12px', md: '14px' }}>
        {information.section}
      </Text>
      <Box h={{ base: '6px', md: '8px' }} />
      <List.Root
        pl={{ base: '16px', md: '20px' }}
        fontSize={{ base: '11px', md: '14px' }}
        color="neutrals.900"
      >
        {information.contents.map((content, contentIndex) => (
          <List.Item key={contentIndex} lineHeight="1.4">
            {content}
          </List.Item>
        ))}
      </List.Root>
    </Flex>
  );
}
